import { apiDelete, apiGet, apiGetResponse, apiPatch, apiPost } from "../../client";
import { ApiConfig } from "../../config";

const VISITING_MANAGEMENT_PATH = `${ApiConfig.CLINIC_BASE}/staff/visiting-management/`;
const TREATMENT_ROOT = `${ApiConfig.CLINIC_BASE}/treatments/`;
const PROCEDURES_PATH = `${ApiConfig.CLINIC_BASE}/procedures/`;
const LABS_PATH = `${ApiConfig.CLINIC_BASE}/labs/`;
const MEDICAL_CERTIFICATES_PATH = `${ApiConfig.CLINIC_BASE}/medical-certificates/`;
const MEDICAL_PRESCRIPTIONS_PATH = `${ApiConfig.CLINIC_BASE}/medical-prescriptions/`;
const MEDICATIONS_PATH = `${ApiConfig.CLINIC_BASE}/medications/`;
const RECOMMENDED_MEDICATIONS_PATH = `${MEDICATIONS_PATH}recommended/`;
const MEDICAL_CODES_SEARCH_PATH = `${ApiConfig.CLINIC_BASE}/medical-codes/search/`;

function parseTreatmentId(treatmentId) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return numericId;
}

/**
 * Search treatments by VN (visit number).
 */
export async function searchTreatments(query, { page = 1, pageSize = 10, patientId } = {}) {
  const params = new URLSearchParams({
    query,
    page: String(page),
    page_size: String(pageSize),
  });
  if (patientId !== undefined && patientId !== null && patientId !== "") {
    params.set("patient_id", String(patientId));
  }
  return apiGet(`${TREATMENT_ROOT}search/?${params.toString()}`);
}

/**
 * Fetch treatment detail by ID.
 */
export async function fetchTreatmentDetail(treatmentId) {
  const numericId = parseTreatmentId(treatmentId);
  return apiGet(`${TREATMENT_ROOT}${numericId}/`);
}

/**
 * Fetch the current visiting management payload for the staff workspace.
 */
export async function fetchVisitingManagement() {
  return apiGet(VISITING_MANAGEMENT_PATH);
}

/**
 * Cancel an in-progress treatment (also removes the queue entry).
 */
export async function cancelTreatment(treatmentId) {
  const numericId = parseTreatmentId(treatmentId);
  return apiDelete(`${TREATMENT_ROOT}${numericId}/`);
}

/**
 * Partially update a treatment record with the provided fields.
 */
export async function updateTreatment(treatmentId, payload) {
  const numericId = parseTreatmentId(treatmentId);
  return apiPatch(`${TREATMENT_ROOT}${numericId}/`, payload);
}

/**
 * Complete a treatment using the same PATCH endpoint as treatment updates.
 */
export async function completeTreatment(treatmentId, payload = {}) {
  return updateTreatment(treatmentId, {
    ...payload,
    status: "completed",
  });
}

/**
 * Update the clinical note rating status for the latest completed clinical note.
 *
 * Note: the backend treatment PATCH endpoint requires `status`.
 */
export async function updateTreatmentClinicalNoteRatingStatus(
  treatmentId,
  clinicalNoteRatingStatus,
  treatmentStatus,
) {
  const numericId = parseTreatmentId(treatmentId);
  const nextStatus = String(treatmentStatus || "").trim();
  if (!nextStatus) {
    throw new Error("สถานะการรักษาไม่ถูกต้อง");
  }
  return apiPatch(`${TREATMENT_ROOT}${numericId}/`, {
    status: nextStatus,
    clinical_note_rating_status: clinicalNoteRatingStatus,
  });
}


/**
 * Search Medical Codes (ICD10) with structured results.
 */
export async function searchMedicalCodes(query) {
  const trimmed = (query ?? "").trim();
  if (!trimmed) {
    return { results: [] };
  }

  const params = new URLSearchParams({ q: trimmed });
  return apiGet(`${MEDICAL_CODES_SEARCH_PATH}?${params.toString()}`);
}

/**
 * Fetch recommended medications based on patient's ICD-10 codes, age, and optional treatment ID.
 */
export async function fetchRecommendedMedications(icd10Codes = [], age, treatmentId = null) {
  const codes = Array.isArray(icd10Codes)
    ? icd10Codes.map((code) => String(code || "").trim()).filter(Boolean)
    : [];

  if (!codes.length) {
    return { results: [] };
  }

  const numericAge = Number.parseInt(age, 10);
  if (!Number.isFinite(numericAge) || numericAge < 0) {
    return { results: [] };
  }

  const params = new URLSearchParams();
  codes.forEach((code) => params.append("icd10_codes", code));
  params.append("age", String(numericAge));
  if (treatmentId) {
    params.append("treatment_id", String(treatmentId));
  }

  return apiGet(`${RECOMMENDED_MEDICATIONS_PATH}?${params.toString()}`);
}

/**
 * Fetch a paginated list of procedures; optional query filters by name.
 */
export async function fetchProcedures(
  query = "",
  { excludePrescriptionId, page = 1, pageSize = 10 } = {}
) {
  const trimmed = (query ?? "").trim();
  const params = new URLSearchParams();
  if (trimmed) {
    params.append("name", trimmed);
  }
  if (excludePrescriptionId) {
    params.append("exclude_prescription_id", excludePrescriptionId);
  }
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  params.append("ordering", "name");
  const queryString = params.toString();
  return apiGet(
    queryString ? `${PROCEDURES_PATH}?${queryString}` : PROCEDURES_PATH,
  );
}

/**
 * Fetch a paginated list of labs; optional query filters by code/name.
 */
export async function fetchLabs(
  query = "",
  { excludePrescriptionId, page = 1, pageSize = 10 } = {},
) {
  const trimmed = (query ?? "").trim();
  const params = new URLSearchParams();
  if (trimmed) {
    params.append("name", trimmed);
  }
  if (excludePrescriptionId) {
    params.append("exclude_prescription_id", excludePrescriptionId);
  }
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  const queryString = params.toString();
  return apiGet(queryString ? `${LABS_PATH}?${queryString}` : LABS_PATH);
}

/**
 * Fetch paginated medical certificate options.
 */
export async function fetchMedicalCertificates({ page, pageSize } = {}) {
  const params = new URLSearchParams();
  if (pageSize) {
    params.append("page_size", pageSize);
  }
  if (page) {
    params.append("page", page);
  }
  const query = params.toString();
  return apiGet(
    query ? `${MEDICAL_CERTIFICATES_PATH}?${query}` : MEDICAL_CERTIFICATES_PATH,
  );
}

/**
 * Fetch medication detail by id.
 */
export async function getMedicationDetail(medicationId, { treatmentId } = {}) {
  const numericId = Number.parseInt(medicationId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสรายการยาไม่ถูกต้อง");
  }
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  const query = Number.isFinite(numericTreatmentId)
    ? `?treatment_id=${numericTreatmentId}`
    : "";
  return apiGet(`${MEDICATIONS_PATH}${numericId}/${query}`);
}

/**
 * Create or update medications in a treatment. Payload must contain a medications array.
 * 
 * payload example 
 * {
    "medications": [
        {
            "medicine_id": 2,
            "usage_instructions": "หลังอาหาร",
            "quantity": 10
        },
    ]  
  }
 */
export async function saveTreatmentMedications(treatmentId, payload) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(`${TREATMENT_ROOT}${numericId}/medications/`, payload);
}

/**
 * Create packages associated with a treatment.
 */
export async function saveTreatmentPackages(treatmentId, payload) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(`${TREATMENT_ROOT}${numericId}/packages/`, payload);
}

/**
 * Create or update procedures associated with a treatment.
 */
export async function saveTreatmentProcedures(treatmentId, payload) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(`${TREATMENT_ROOT}${numericId}/procedures/`, payload);
}

/**
 * Create or update medical certificates associated with a treatment.
 */
export async function saveTreatmentMedicalCertificates(treatmentId, payload) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(
    `${TREATMENT_ROOT}${numericId}/medical-certificates/`,
    payload,
  );
}

export async function applyMedicationSet(treatmentId, medicationSetId) {
  const numericId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  const payload = {
    medication_set_id: medicationSetId,
  };
  return apiPost(`${TREATMENT_ROOT}${numericId}/medication-sets/`, payload);
}

/**
 * Apply medications from a historical treatment to the current treatment.
 */
export async function applyRemedToTreatment(treatmentId, sourceTreatmentId) {
  const numericId = parseTreatmentId(treatmentId);
  const numericSourceId = parseTreatmentId(sourceTreatmentId);
  const payload = {
    source_treatment_id: numericSourceId,
  };
  return apiPost(`${TREATMENT_ROOT}${numericId}/remed/`, payload);
}

/**
 * Remove a medication entry from a treatment.
 */
export async function deleteTreatmentMedication(
  treatmentId,
  treatmentMedicationId,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  const numericMedicationId = Number.parseInt(treatmentMedicationId, 10);
  if (!Number.isFinite(numericMedicationId)) {
    throw new Error("รหัสรายการยาไม่ถูกต้อง");
  }
  return apiDelete(
    `${TREATMENT_ROOT}${numericTreatmentId}/medications/${numericMedicationId}/`,
  );
}

/**
 * Remove a package entry from a treatment.
 */
export async function deleteTreatmentPackage(treatmentId, treatmentPackageId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  const numericPackageId = Number.parseInt(treatmentPackageId, 10);
  if (!Number.isFinite(numericPackageId)) {
    throw new Error("รหัสแพ็กเกจที่สั่งไม่ถูกต้อง");
  }
  return apiDelete(
    `${TREATMENT_ROOT}${numericTreatmentId}/packages/${numericPackageId}/`,
  );
}

/**
 * Update treatment medication.
 */
export async function updateTreatmentMedication(
  treatmentId,
  treatmentMedicationId,
  payload,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  if (!Number.isFinite(treatmentMedicationId)) {
    throw new Error("รหัสรายการยาไม่ถูกต้อง");
  }
  return apiPatch(
    `${TREATMENT_ROOT}${numericTreatmentId}/medications/${treatmentMedicationId}/`,
    payload,
  );
}

/**
 * Update a medication dispense item used by the pharmacy workflow.
 */
export async function updateMedicationDispenseItem(
  treatmentId,
  medicationDispenseItemId,
  payload,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  const numericDispenseItemId = Number.parseInt(medicationDispenseItemId, 10);
  if (!Number.isFinite(numericDispenseItemId)) {
    throw new Error("รหัสรายการจัดยาไม่ถูกต้อง");
  }

  return apiPatch(
    `${TREATMENT_ROOT}${numericTreatmentId}/medication-dispense-items/${numericDispenseItemId}/`,
    payload,
  );
}

/**
 * Mark dispensing of all medications in a treatment as complete.
 */
export async function completeTreatmentDispense(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  return apiPost(`${TREATMENT_ROOT}${numericTreatmentId}/complete-dispensing/`);
}

/**
 * Mark that treatment preparation has started.
 */
export async function startPreparation(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  return apiPost(`${TREATMENT_ROOT}${numericTreatmentId}/start-preparation/`);
}

/**
 * Cancel dispensing of all medications in a treatment.
 */
export async function cancelTreatmentDispense(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  return apiPost(`${TREATMENT_ROOT}${numericTreatmentId}/cancel-dispensing/`);
}

/**
 * Request a medication change for a treatment.
 */
export async function requestMedicationChange(treatmentId, payload) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/medication-edit-requests/`,
    payload,
  );
}

/**
 * Fetch medication change requests.
 */

export async function fetchMedicationChangeRequests({ status, page, pageSize = 10 } = {}) {
  const params = new URLSearchParams();
  if (status) {
    params.append("status", status);
  }
  if (page) {
    params.append("page", String(page));
  }
  if (pageSize) {
    params.append("page_size", String(pageSize));
  }
  const query = params.toString();
  const path = `${TREATMENT_ROOT}medication-review-requests/`;
  return apiGet(query ? `${path}?${query}` : path);
}

/**
 * Process a medication change request.
 */
export async function processMedicationChangeRequest(
  medicationReviewRequestId,
  payload,
) {
  const numericRequestId = Number.parseInt(medicationReviewRequestId, 10);
  if (!Number.isFinite(numericRequestId)) {
    throw new Error("รหัสคำขอแก้ไขยาไม่ถูกต้อง");
  }
  return apiPatch(
    `${TREATMENT_ROOT}medication-review-requests/${numericRequestId}/`,
    payload,
  );
}

/**
 * Remove a procedure entry from a treatment.
 */
export async function deleteTreatmentProcedure(
  treatmentId,
  treatmentProcedureId,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  const numericProcedureId = Number.parseInt(treatmentProcedureId, 10);
  if (!Number.isFinite(numericProcedureId)) {
    throw new Error("รหัสหัตถการไม่ถูกต้อง");
  }
  return apiDelete(
    `${TREATMENT_ROOT}${numericTreatmentId}/procedures/${numericProcedureId}/`,
  );
}

/**
 * Remove a medical certificate entry from a treatment.
 */
export async function deleteTreatmentMedicalCertificate(
  treatmentId,
  treatmentMedicalCertificateId,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  const numericCertificateId = Number.parseInt(
    treatmentMedicalCertificateId,
    10,
  );
  if (!Number.isFinite(numericCertificateId)) {
    throw new Error("รหัสใบรับรองแพทย์ไม่ถูกต้อง");
  }
  return apiDelete(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-certificates/${numericCertificateId}/`,
  );
}

/**
 * Complete printing medical certificates for a treatment.
 */
export async function completeTreatmentMedicationCertificatesPrinting(
  treatmentId,
  treatmentMedicalCertificateIds,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  const payload = {};
  if (
    Array.isArray(treatmentMedicalCertificateIds) &&
    treatmentMedicalCertificateIds.length > 0
  ) {
    payload.treatment_medical_certificate_ids = treatmentMedicalCertificateIds;
  }

  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-certificates/print/`,
    payload,
  );
}

/**
 * Complete printing treatment memos.
 *
 * @param {number|string} treatmentId
 * @param {Array<number|string>} [treatmentMemoIds]
 * @returns {Promise<any>}
 */
export async function completeTreatmentMemosPrinting(
  treatmentId,
  treatmentMemoIds,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  const payload = {};
  if (
    Array.isArray(treatmentMemoIds) &&
    treatmentMemoIds.length > 0
  ) {
    payload.treatment_memo_ids = treatmentMemoIds;
  }

  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/memos/print/`,
    payload,
  );
}

/**
 * Notify the queue to call the patient again.
 */
export async function notifyTreatmentQueue(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(`${TREATMENT_ROOT}${numericTreatmentId}/notify-queue/`);
}

/**
 * Notify workspace (Google Chat) for medical certificates upon completion & payment in StaffGenMedicalCertificateModal.
 */
export async function notifyMedicalCertificateWorkspace(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/notify-medical-certificate/`,
  );
}

/**
 * Fetch certificate payload for editing.
 */
export async function fetchTreatmentMedicalCertificate(
  treatmentId,
  treatmentMedicalCertificateId,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  const numericCertificateId = Number.parseInt(
    treatmentMedicalCertificateId,
    10,
  );
  if (
    !Number.isFinite(numericTreatmentId) ||
    !Number.isFinite(numericCertificateId)
  ) {
    throw new Error("Invalid ID");
  }
  return apiGet(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-certificates/${numericCertificateId}/`,
  );
}

/**
 * Update certificate custom data.
 */
export async function updateTreatmentMedicalCertificateDetail(
  treatmentId,
  treatmentMedicalCertificateId,
  payload,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  const numericCertificateId = Number.parseInt(
    treatmentMedicalCertificateId,
    10,
  );
  if (
    !Number.isFinite(numericTreatmentId) ||
    !Number.isFinite(numericCertificateId)
  ) {
    throw new Error("Invalid ID");
  }
  return apiPatch(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-certificates/${numericCertificateId}/`,
    payload,
  );
}

/**
 * Summarize treatment clinical note with options.
 */
export async function summarizeTreatmentClinicalNote(
  treatmentId,
  payload = {},
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }
  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/clinical-note/summarize/`,
    payload,
  );
}

/**
 * Manually trigger a realtime broadcast for a treatment.
 * This is used to sync data across devices after batch operations are completed.
 */
export async function broadcastVisitSync(treatmentId) {
  const numericId = parseTreatmentId(treatmentId);
  return apiPost(`${ApiConfig.CLINIC_BASE}/attachments/broadcast-sync/`, {
    treatment_id: numericId,
  });
}

/**
 * Fetch memos for a treatment.
 */
export async function fetchTreatmentMemos(treatmentId) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  return apiGet(`${TREATMENT_ROOT}${numericTreatmentId}/memos/`);
}

/**
 * Create a memo for a treatment.
 */
export async function createTreatmentMemo(treatmentId, payload) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  return apiPost(`${TREATMENT_ROOT}${numericTreatmentId}/memos/`, payload);
}

/**
 * Update a memo for a treatment.
 */
export async function updateTreatmentMemo(treatmentId, memoId, payload) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  const numericMemoId = Number.parseInt(memoId, 10);
  if (!Number.isFinite(numericMemoId)) {
    throw new Error("รหัสใบบันทึกข้อความไม่ถูกต้อง");
  }
  return apiPatch(
    `${TREATMENT_ROOT}${numericTreatmentId}/memos/${numericMemoId}/`,
    payload,
  );
}

/**
 * Delete a memo from a treatment.
 */
export async function deleteTreatmentMemo(treatmentId, memoId) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  const numericMemoId = Number.parseInt(memoId, 10);
  if (!Number.isFinite(numericMemoId)) {
    throw new Error("รหัสใบบันทึกข้อความไม่ถูกต้อง");
  }
  return apiDelete(
    `${TREATMENT_ROOT}${numericTreatmentId}/memos/${numericMemoId}/`,
  );
}

/**
 * Fetch treatment memo PDF response.
 */
export async function fetchTreatmentMemoPdf(treatmentId, memoId) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  const numericMemoId = Number.parseInt(memoId, 10);
  if (!Number.isFinite(numericMemoId)) {
    throw new Error("รหัสใบบันทึกข้อความไม่ถูกต้อง");
  }
  return apiGetResponse(
    `${TREATMENT_ROOT}${numericTreatmentId}/memos/${numericMemoId}/pdf/`,
  );
}

/**
 * Create a medical prescription for a treatment.
 */
export async function createMedicalPrescription(treatmentId, payload = {}) {
  const numericTreatmentId = parseTreatmentId(treatmentId);
  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-prescriptions/`,
    payload,
  );
}

/**
 * Fetch medical prescription detail by ID.
 */
export async function fetchMedicalPrescriptionDetail(prescriptionId) {
  const numericId = Number.parseInt(prescriptionId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสใบสั่งยาไม่ถูกต้อง");
  }
  return apiGet(`${MEDICAL_PRESCRIPTIONS_PATH}${numericId}/`);
}

/**
 * Update a medical prescription.
 */
export async function updateMedicalPrescription(prescriptionId, payload) {
  const numericId = Number.parseInt(prescriptionId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสใบสั่งยาไม่ถูกต้อง");
  }
  return apiPatch(
    `${MEDICAL_PRESCRIPTIONS_PATH}${numericId}/`,
    payload,
  );
}

/**
 * Delete a medical prescription.
 */
export async function deleteMedicalPrescription(prescriptionId) {
  const numericId = Number.parseInt(prescriptionId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสใบสั่งยาไม่ถูกต้อง");
  }
  return apiDelete(
    `${MEDICAL_PRESCRIPTIONS_PATH}${numericId}/`,
  );
}

/**
 * Fetch medical prescription PDF as a Response object.
 */
export async function fetchMedicalPrescriptionPdf(prescriptionId) {
  const numericId = Number.parseInt(prescriptionId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสใบสั่งยาไม่ถูกต้อง");
  }
  return apiGetResponse(
    `${MEDICAL_PRESCRIPTIONS_PATH}${numericId}/pdf/`,
  );
}

/**
 * Complete printing medical prescriptions.
 *
 * @param {number|string} treatmentId
 * @param {Array<number|string>} [medicalPrescriptionIds]
 * @returns {Promise<any>}
 */
export async function completeTreatmentMedicalPrescriptionsPrinting(
  treatmentId,
  medicalPrescriptionIds,
) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (!Number.isFinite(numericTreatmentId)) {
    throw new Error("รหัสการรักษาไม่ถูกต้อง");
  }

  const payload = {};
  if (Array.isArray(medicalPrescriptionIds) && medicalPrescriptionIds.length > 0) {
    payload.medical_prescription_ids = medicalPrescriptionIds.map((id) =>
      Number.parseInt(id, 10)
    );
  }

  return apiPost(
    `${TREATMENT_ROOT}${numericTreatmentId}/medical-prescriptions/print/`,
    payload
  );
}

